import {useState, useMemo, useCallback} from 'react';
import {useAppDispatch, useAppSelector} from '@/store';
import {setActivityFormData} from '@/store/slices/activitySlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {
  patchActivityStatus,
  getActivityReportFile
} from '@/services/activities';
import {
  mapActivityStatusFromPatchResponse,
  getActivityPublishToggleAction,
  isActivityPublishEnabledStatus,
  isActivityUnpublishEnabledStatus,
  isActivityDuplicateEnabledStatus,
  isActivityDownloadDisabledStatus,
  TOGGLE_ACTION
} from '@/helpers/activityUtils';
import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useNavigate} from 'react-router-dom';
import {logger} from '@/helpers/logger';
import {getActivityReviewActionConfig} from '@/containers/Activity/ActivityReview/constants/activityReviewActionConfig';

const canToggleActivityStatus = (status, isactive) => {
  if (isactive === 0) {
    return isActivityUnpublishEnabledStatus(status);
  }
  if (isactive === 1) {
    return isActivityPublishEnabledStatus(status);
  }
  return false;
};

/**
 * View-mode action buttons: PATCH publish/unpublish toggle, Duplicate, Download.
 * Footer publish (create/update) lives in useActivityReview.handlePublish — not here.
 *
 * @param {'survey' | 'video' | 'freeKick' | 'wheelOfFortune' | 'backgammon' | null} activityKind
 */
const useActivityReviewActions = (activityKind) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {downloadFromBlobUrl} = useAzureBlob();
  const activityFormData = useAppSelector((state) => state.activity.formData);
  const config = getActivityReviewActionConfig(activityKind);

  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [actionAlert, setActionAlert] = useState(null);

  const activityStatus = activityFormData?.status;

  const publishToggle = useMemo(
    () => getActivityPublishToggleAction(activityStatus),
    [activityStatus]
  );

  const isDuplicateEnabled = useMemo(
    () => isActivityDuplicateEnabledStatus(activityStatus),
    [activityStatus]
  );

  const isDownloadEnabled = useMemo(
    () => !isActivityDownloadDisabledStatus(activityStatus),
    [activityStatus]
  );

  const handlePublishToggleClick = useCallback(() => {
    const toggle = getActivityPublishToggleAction(activityFormData?.status);
    if (!toggle.enabled) return;
    if (toggle.action === TOGGLE_ACTION.UNPUBLISH) {
      setShowUnpublishModal(true);
    } else if (toggle.action === TOGGLE_ACTION.PUBLISH) {
      setShowPublishModal(true);
    }
  }, [activityFormData?.status]);

  const handleCloseUnpublishModal = useCallback(() => {
    setShowUnpublishModal(false);
  }, []);

  const handleClosePublishModal = useCallback(() => {
    setShowPublishModal(false);
  }, []);

  const handleDismissAlert = useCallback(() => {
    setActionAlert(null);
  }, []);

  const toggleActiveStatus = useCallback(
    async (isactive) => {
      if (!config) return;

      if (!canToggleActivityStatus(activityFormData?.status, isactive)) {
        return;
      }

      const activityId = activityFormData?.id || activityFormData?.activityId;

      if (!activityId) {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Activity ID is required',
            type: 'error'
          })
        );
        return;
      }

      try {
        const response = await patchActivityStatus(activityId, {
          isactive
        });

        if (!response?.success) {
          dispatch(
            setToastNotifications({
              show: true,
              message:
                response?.message ||
                (isactive === 0
                  ? config.failedUnpublish
                  : config.failedPublish),
              type: 'error'
            })
          );
          return;
        }

        const mappedActivityData = mapActivityStatusFromPatchResponse(
          response,
          activityFormData
        );

        dispatch(
          setActivityFormData({
            ...mappedActivityData,
            isactive
          })
        );
        setActionAlert(
          isactive === 0
            ? {
                type: 'caution',
                title: config.unpublishedAlertTitle,
                subTitle: config.unpublishedAlertSubTitle
              }
            : {
                type: 'successGreen',
                title: config.publishedAlertTitle,
                subTitle: ''
              }
        );
      } catch (error) {
        logger.error(
          isactive === 0 ? config.errorUnpublishing : config.errorPublishing,
          error
        );
        dispatch(
          setToastNotifications({
            show: true,
            message:
              error?.response?.data?.message ||
              error?.message ||
              (isactive === 0 ? config.failedUnpublish : config.failedPublish),
            type: 'error'
          })
        );
      }
    },
    [activityFormData, dispatch, config]
  );

  const handleConfirmUnpublish = useCallback(async () => {
    setShowUnpublishModal(false);
    await toggleActiveStatus(0);
  }, [toggleActiveStatus]);

  const handleConfirmPublish = useCallback(async () => {
    setShowPublishModal(false);
    await toggleActiveStatus(1);
  }, [toggleActiveStatus]);

  const handleDuplicate = useCallback(() => {
    const duplicateData = {
      ...activityFormData,
      id: undefined,
      uuid: undefined,
      activityId: undefined,
      status: undefined,
      activityName: ''
    };

    // AC3: For Free Kick and Backgammon games, handle date validation
    if (activityKind === 'freeKick' || activityKind === 'backgammon') {
      const now = new Date();
      const startDate = activityFormData?.startDate
        ? new Date(activityFormData.startDate)
        : null;

      // If start date is in the past or today, reset dates to null
      // User must update to a future date before saving/publishing
      if (startDate && startDate <= now) {
        duplicateData.startDate = null;
        duplicateData.endDate = null;
      }
      // Activity Type, Segments are already preserved in duplicateData
      // activityName is cleared to ensure uniqueness
    }

    dispatch(setActivityFormData(duplicateData));
    navigate(ROUTES.ACTIVITY_ADD);
  }, [activityFormData, dispatch, navigate, activityKind]);

  const handleDownload = useCallback(async () => {
    if (!config) return;

    try {
      const activityId = activityFormData?.id || activityFormData?.activityId;
      if (!activityId) return;

      const response = await getActivityReportFile(activityId);

      if (!response?.success) {
        dispatch(
          setToastNotifications({
            show: true,
            message: response?.message || config.failedDownload,
            type: 'error'
          })
        );
        return;
      }

      if (response?.totalRecords === 0) {
        dispatch(
          setToastNotifications({
            show: true,
            message: config.noRecordsFound,
            type: 'info'
          })
        );
        return;
      }

      if (response?.blobUrl && response?.sasToken) {
        const csvText = await downloadFromBlobUrl(
          response.blobUrl,
          response.sasToken
        );
        if (csvText) {
          const fileName = `${config.downloadFilePrefix}${getDateWithTimeString()}`;
          downloadCSVFiles(csvText, fileName);
        } else {
          dispatch(
            setToastNotifications({
              show: true,
              message: 'Failed to download report data',
              type: 'error'
            })
          );
        }
      } else {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Report data not available',
            type: 'error'
          })
        );
      }
    } catch (error) {
      logger.error(config.errorDownloading, error);
      dispatch(
        setToastNotifications({
          show: true,
          message: config.failedDownload,
          type: 'error'
        })
      );
    }
  }, [activityFormData, dispatch, config, downloadFromBlobUrl]);

  return {
    publishToggle,
    isDuplicateEnabled,
    isDownloadEnabled,
    showUnpublishModal,
    showPublishModal,
    actionAlert,
    handlePublishToggleClick,
    handleCloseUnpublishModal,
    handleClosePublishModal,
    handleConfirmUnpublish,
    handleConfirmPublish,
    handleDuplicate,
    handleDownload,
    handleDismissAlert
  };
};

export default useActivityReviewActions;
